import { toOrderStatus, type OrderStatus } from "@nexus/order-status";
import { Icon, cn } from "@nexus/ui";
import { useTranslation } from "react-i18next";

import {
  describeDeadline,
  localTodayYmd,
} from "@/features/marketplace/lib/order-deadline";
import { isPartnerActionableStatus } from "@/features/marketplace/partner/lib/partner-work-queue";
import type { PartnerOrder } from "@/features/marketplace/types/marketplace.types";

interface PartnerQueueOverviewProps {
  orders: readonly PartnerOrder[];
}

interface PartnerQueueSnapshot {
  openOrders: number;
  openScripts: number;
  scriptCount: number;
  needsAction: number;
  overdue: number;
  awaitingReview: number;
  stages: Record<PartnerStage, number>;
  deadlines: Record<PartnerDeadline, number>;
}

type PartnerStage = "production" | "revision" | "review" | "delivered";
type PartnerDeadline = "overdue" | "today" | "soon" | "later" | "unscheduled";

interface DistributionItem {
  id: string;
  label: string;
  value: number;
  color: string;
}

const REVIEW_STATUSES: ReadonlySet<OrderStatus> = new Set([
  "SUBMITTED_FOR_FEEDBACK",
  "SUBMITTED_FOR_FINAL_APPROVAL",
]);

const METRIC_VALUE_CLASS = "text-2xl font-semibold leading-8 tabular-nums";

export function PartnerQueueOverview({ orders }: PartnerQueueOverviewProps) {
  const { t, i18n } = useTranslation();
  const todayYmd = localTodayYmd();
  const snapshot = summarizePartnerQueue(orders, todayYmd);
  const formatter = new Intl.NumberFormat(
    i18n.resolvedLanguage ?? i18n.language,
  );
  const metrics = [
    {
      id: "open-orders",
      label: t(
        "marketplace.partner.overview.openOrders",
        "Open orders",
      ),
      value: snapshot.openOrders,
    },
    {
      id: "open-scripts",
      label: t(
        "marketplace.partner.overview.openScripts",
        "Open scripts",
      ),
      value: snapshot.openScripts,
    },
    {
      id: "needs-action",
      label: t(
        "marketplace.partner.overview.needsAction",
        "Needs my action",
      ),
      value: snapshot.needsAction,
      tone: "text-error",
      icon: true,
    },
    {
      id: "overdue",
      label: t("marketplace.partner.overview.overdue", "Overdue"),
      value: snapshot.overdue,
      tone: "text-warning",
      icon: true,
    },
    {
      id: "awaiting-review",
      label: t(
        "marketplace.partner.overview.awaitingReview",
        "Waiting for feedback",
      ),
      value: snapshot.awaitingReview,
    },
  ];
  const stageItems: DistributionItem[] = [
    {
      id: "production",
      label: t(
        "marketplace.partner.overview.stages.production",
        "Ready to produce",
      ),
      value: snapshot.stages.production,
      color: "var(--chart-blue)",
    },
    {
      id: "revision",
      label: t(
        "marketplace.partner.overview.stages.revision",
        "Revision requested",
      ),
      value: snapshot.stages.revision,
      color: "var(--chart-orange)",
    },
    {
      id: "review",
      label: t(
        "marketplace.partner.overview.stages.review",
        "With client and Adge",
      ),
      value: snapshot.stages.review,
      color: "var(--chart-purple)",
    },
    {
      id: "delivered",
      label: t(
        "marketplace.partner.overview.stages.delivered",
        "Delivered",
      ),
      value: snapshot.stages.delivered,
      color: "var(--chart-green)",
    },
  ];
  const deadlineItems: DistributionItem[] = [
    {
      id: "overdue",
      label: t(
        "marketplace.partner.overview.deadlines.overdue",
        "Overdue",
      ),
      value: snapshot.deadlines.overdue,
      color: "var(--error)",
    },
    {
      id: "today",
      label: t(
        "marketplace.partner.overview.deadlines.today",
        "Due today",
      ),
      value: snapshot.deadlines.today,
      color: "var(--warning)",
    },
    {
      id: "soon",
      label: t(
        "marketplace.partner.overview.deadlines.soon",
        "Next 7 days",
      ),
      value: snapshot.deadlines.soon,
      color: "var(--chart-yellow)",
    },
    {
      id: "later",
      label: t(
        "marketplace.partner.overview.deadlines.later",
        "Later",
      ),
      value: snapshot.deadlines.later,
      color: "var(--chart-blue)",
    },
    {
      id: "unscheduled",
      label: t(
        "marketplace.partner.overview.deadlines.unscheduled",
        "Not scheduled",
      ),
      value: snapshot.deadlines.unscheduled,
      color: "var(--muted-foreground)",
    },
  ];

  return (
    <section
      aria-labelledby="partner-queue-overview-title"
      className="overflow-hidden rounded-md border border-border bg-card"
    >
      <header className="flex min-h-11 flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-2">
        <h2
          id="partner-queue-overview-title"
          className="font-mono text-[10px] uppercase tracking-label text-muted-foreground"
        >
          {t(
            "marketplace.partner.overview.title",
            "Production snapshot",
          )}
        </h2>
        <span className="text-caption tabular-nums text-muted-foreground">
          {t("marketplace.partner.overview.loadedOrders", {
            count: orders.length,
            defaultValue_one: "{{count}} loaded order",
            defaultValue_other: "{{count}} loaded orders",
          })}
        </span>
      </header>

      <dl className="grid grid-cols-2 gap-px border-b border-border bg-border sm:grid-cols-3 lg:grid-cols-5">
        {metrics.map((metric) => (
          <div key={metric.id} className="min-w-0 bg-card px-4 py-3">
            <dt className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-label text-muted-foreground">
              {metric.icon ? (
                <Icon
                  name="alert-circle"
                  size="xs"
                  color="inherit"
                  aria-hidden
                />
              ) : null}
              <span className="truncate">{metric.label}</span>
            </dt>
            <dd
              className={cn(
                METRIC_VALUE_CLASS,
                metric.value === 0
                  ? "font-normal text-muted-foreground"
                  : metric.tone,
              )}
            >
              {formatter.format(metric.value)}
            </dd>
          </div>
        ))}
      </dl>

      <div className="grid divide-y divide-border md:grid-cols-2 md:divide-x md:divide-y-0">
        <DistributionChart
          title={t(
            "marketplace.partner.overview.stages.title",
            "Work stage",
          )}
          summary={t("marketplace.partner.overview.scriptCount", {
            count: snapshot.scriptCount,
            defaultValue_one: "{{count}} script",
            defaultValue_other: "{{count}} scripts",
          })}
          items={stageItems}
          formatter={formatter}
        />
        <DistributionChart
          title={t(
            "marketplace.partner.overview.deadlines.title",
            "Deadline pressure",
          )}
          summary={t("marketplace.partner.overview.openScriptCount", {
            count: snapshot.openScripts,
            defaultValue_one: "{{count}} open script",
            defaultValue_other: "{{count}} open scripts",
          })}
          items={deadlineItems}
          formatter={formatter}
        />
      </div>
    </section>
  );
}

function DistributionChart({
  title,
  summary,
  items,
  formatter,
}: {
  title: string;
  summary: string;
  items: DistributionItem[];
  formatter: Intl.NumberFormat;
}) {
  const total = items.reduce((sum, item) => sum + item.value, 0);
  let offset = 0;

  return (
    <section aria-label={title} className="min-w-0 p-4">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-xs font-medium text-foreground">{title}</h3>
        <span className="text-caption tabular-nums text-muted-foreground">
          {summary}
        </span>
      </div>
      <svg
        viewBox="0 0 100 8"
        preserveAspectRatio="none"
        aria-hidden
        className="mt-3 h-2.5 w-full overflow-hidden rounded-sm"
      >
        <rect width="100" height="8" rx="1.5" fill="var(--muted)" />
        {items.map((item) => {
          const width = total > 0 ? (item.value / total) * 100 : 0;
          const x = offset;
          offset += width;
          return width > 0 ? (
            <rect
              key={item.id}
              x={x}
              width={width}
              height="8"
              fill={item.color}
            />
          ) : null;
        })}
      </svg>
      <ul className="mt-3 grid grid-cols-2 gap-x-5 gap-y-2">
        {items.map((item) => (
          <li
            key={item.id}
            className="flex min-w-0 items-center gap-2 text-caption"
          >
            <svg
              viewBox="0 0 8 8"
              aria-hidden
              className="size-2 shrink-0"
            >
              <rect width="8" height="8" rx="1.5" fill={item.color} />
            </svg>
            <span className="min-w-0 flex-1 truncate text-muted-foreground">
              {item.label}
            </span>
            <span className="tabular-nums text-foreground">
              {formatter.format(item.value)}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function summarizePartnerQueue(
  orders: readonly PartnerOrder[],
  todayYmd: string,
): PartnerQueueSnapshot {
  const snapshot: PartnerQueueSnapshot = {
    openOrders: 0,
    openScripts: 0,
    scriptCount: 0,
    needsAction: 0,
    overdue: 0,
    awaitingReview: 0,
    stages: { production: 0, revision: 0, review: 0, delivered: 0 },
    deadlines: { overdue: 0, today: 0, soon: 0, later: 0, unscheduled: 0 },
  };

  for (const order of orders) {
    let hasOpenScript = false;
    for (const script of order.scripts) {
      const status = toOrderStatus(script.fulfillmentStatus);
      if (status === "CANCELLED") continue;
      snapshot.scriptCount += 1;
      const isDelivered = status === "DELIVERED";
      if (isDelivered) {
        snapshot.stages.delivered += 1;
        continue;
      }

      hasOpenScript = true;
      snapshot.openScripts += 1;
      if (isPartnerActionableStatus(status)) snapshot.needsAction += 1;
      if (REVIEW_STATUSES.has(status)) snapshot.awaitingReview += 1;

      if (status === "REVISION_REQUESTED") snapshot.stages.revision += 1;
      else if (REVIEW_STATUSES.has(status)) snapshot.stages.review += 1;
      else snapshot.stages.production += 1;

      const deadline = describeDeadline(
        script.expectedDeliveryDate ?? order.etaDate,
        todayYmd,
      );
      if (!deadline) snapshot.deadlines.unscheduled += 1;
      else if (deadline.tone === "overdue") {
        snapshot.deadlines.overdue += 1;
        snapshot.overdue += 1;
      } else if (deadline.tone === "today") snapshot.deadlines.today += 1;
      else if (deadline.tone === "soon") snapshot.deadlines.soon += 1;
      else snapshot.deadlines.later += 1;
    }
    if (hasOpenScript) snapshot.openOrders += 1;
  }

  return snapshot;
}
